/*
  Stallion 53 bottle cursor (snippets/snip_bottle_cursor.liquid).
  A bottle trails the pointer with a soft lag, leans with the movement and bobs while it travels.
  States (classes on .bottle-cursor): is-active, is-over-card, is-over-cta, is-idle, is-pressed.
  Desktop with a mouse only; with reduced motion it follows without lag, lean or mist.
*/
(() => {
  const bottle = document.querySelector('[data-bottle-cursor]');
  if (!bottle || bottle.dataset.ready) return;
  bottle.dataset.ready = 'true';

  const desktopMouse = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
  if (!desktopMouse.matches) return;

  document.body.classList.add('has-bottle-cursor');

  const figure = bottle.querySelector('.bottle-cursor__figure');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // The bottle sits below-right of the arrow, like dossier.eu's doll
  const OFFSET_X = 20;
  const OFFSET_Y = 18;
  // Nozzle position inside the 52 x 95 bottle once it has tipped over (is-pressed: rotate -14deg, scale 1.06 x 0.9)
  const NOZZLE_X = 6;
  const NOZZLE_Y = 14;

  const HIDE_SELECTOR =
    'input, textarea, select, [contenteditable="true"], iframe, dialog, [role="dialog"], [aria-modal="true"], [popover], cart-drawer, .drawer';
  const CTA_SELECTOR =
    'button, [type="submit"], .btn, .button, [name="add"], [class*="add-to-cart"], [class*="add_to_cart"]';
  const CARD_SELECTOR = '.card, [class*="product-card"], .pdp-dossier-families__card';

  const IDLE_SWAY_MS = 4000;
  const IDLE_HIDE_MS = 10000;
  const FOLLOW_TAU = 0.11; // seconds to close ~63% of the gap
  const LEAN_PER_PX_S = 0.022;
  const LEAN_MAX = 16;
  const LEAN_STIFFNESS = 140;
  const LEAN_DAMPING = 14;
  const BOB_MAX_PX = 4;
  const BOB_FULL_SPEED = 900;
  const STEPS_PER_SECOND = 5;

  const target = { x: 0, y: 0 };
  const pos = { x: 0, y: 0 };
  let lean = 0;
  let leanVelocity = 0;
  let bobAmp = 0;
  let bobPhase = 0;
  let lastFrame = 0;
  let rafId = 0;
  let started = false;
  let swayTimer = 0;
  let hideTimer = 0;

  const setState = (name, on) => bottle.classList.toggle(name, on);
  const place = (x, y) => {
    bottle.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  };

  function frame(now) {
    const dt = Math.min((now - lastFrame) / 1000, 0.05) || 0.016;
    lastFrame = now;

    const k = 1 - Math.exp(-dt / FOLLOW_TAU);
    const prevX = pos.x;
    const prevY = pos.y;
    pos.x += (target.x - pos.x) * k;
    pos.y += (target.y - pos.y) * k;

    const vx = (pos.x - prevX) / dt;
    const vy = (pos.y - prevY) / dt;
    const speed = Math.hypot(vx, vy);

    // Spring the lean towards the horizontal speed so it swings and settles
    const targetLean = Math.max(-LEAN_MAX, Math.min(LEAN_MAX, vx * LEAN_PER_PX_S));
    leanVelocity += ((targetLean - lean) * LEAN_STIFFNESS - leanVelocity * LEAN_DAMPING) * dt;
    lean += leanVelocity * dt;

    const wantedAmp = Math.min(speed / BOB_FULL_SPEED, 1) * BOB_MAX_PX;
    bobAmp += (wantedAmp - bobAmp) * Math.min(dt * 10, 1);
    bobPhase += dt * Math.PI * 2 * STEPS_PER_SECOND;
    const bob = Math.abs(Math.sin(bobPhase)) * bobAmp;

    place(pos.x, pos.y);
    figure.style.transform = `translateY(${-bob}px) rotate(${lean}deg)`;

    const settled =
      Math.abs(target.x - pos.x) < 0.3 &&
      Math.abs(target.y - pos.y) < 0.3 &&
      Math.abs(lean) < 0.05 &&
      Math.abs(leanVelocity) < 0.5 &&
      bobAmp < 0.05;
    if (settled) {
      pos.x = target.x;
      pos.y = target.y;
      lean = 0;
      leanVelocity = 0;
      bobAmp = 0;
      place(pos.x, pos.y);
      figure.style.transform = '';
      rafId = 0;
      return;
    }
    rafId = requestAnimationFrame(frame);
  }

  function hide() {
    setState('is-idle', false);
    setState('is-active', false);
  }

  window.addEventListener(
    'mousemove',
    (event) => {
      const el = event.target instanceof Element ? event.target : null;
      const hidden = Boolean(el && el.closest(HIDE_SELECTOR));
      const overCta = !hidden && Boolean(el && el.closest(CTA_SELECTOR));
      const overCard = !hidden && !overCta && Boolean(el && el.closest(CARD_SELECTOR));

      target.x = event.clientX + OFFSET_X;
      target.y = event.clientY + OFFSET_Y;
      if (!started) {
        started = true;
        pos.x = target.x;
        pos.y = target.y;
        place(pos.x, pos.y);
      }

      clearTimeout(swayTimer);
      clearTimeout(hideTimer);
      setState('is-idle', false);
      setState('is-active', !hidden);
      setState('is-over-cta', overCta);
      setState('is-over-card', overCard);

      if (reducedMotion) {
        place(target.x, target.y);
        return;
      }

      if (!hidden) {
        swayTimer = setTimeout(() => setState('is-idle', true), IDLE_SWAY_MS);
        hideTimer = setTimeout(hide, IDLE_HIDE_MS);
      }
      if (!rafId) {
        lastFrame = performance.now();
        rafId = requestAnimationFrame(frame);
      }
    },
    { passive: true }
  );

  document.addEventListener('mouseleave', () => {
    clearTimeout(swayTimer);
    clearTimeout(hideTimer);
    hide();
  });

  if (reducedMotion) return;

  // Click: the bottle tips towards the arrow and sprays a fine mist at the click point:
  // a soft haze cloud plus a narrow cone of tiny fast droplets, like a real atomiser
  function spray(mouseX, mouseY) {
    const nozzleX = pos.x + NOZZLE_X;
    const nozzleY = pos.y + NOZZLE_Y;
    const aim = Math.atan2(mouseY - nozzleY, mouseX - nozzleX);

    const mist = document.createElement('div');
    mist.className = 'bottle-cursor-mist';
    mist.style.transform = `translate3d(${nozzleX}px, ${nozzleY}px, 0)`;

    const addHaze = (distance, delay, width) => {
      const haze = document.createElement('i');
      haze.style.setProperty('--dx', `${Math.cos(aim) * distance}px`);
      haze.style.setProperty('--dy', `${Math.sin(aim) * distance}px`);
      haze.style.setProperty('--rot', `${aim}rad`);
      haze.style.setProperty('--delay', `${delay}ms`);
      haze.style.setProperty('--w', `${width}px`);
      mist.appendChild(haze);
    };
    addHaze(46, 0, 30);
    addHaze(30, 40, 22);

    const DROPS = 44;
    const CONE = 0.32; // radians either side of the aim
    for (let i = 0; i < DROPS; i += 1) {
      // Bias towards the centre of the cone, like a real spray pattern
      const spread = (Math.random() + Math.random() - 1) * CONE;
      const angle = aim + spread;
      const distance = 28 + Math.random() * 48;
      const drop = document.createElement('span');
      drop.style.setProperty('--size', `${1.2 + Math.random() * 2.3}px`);
      drop.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
      drop.style.setProperty('--dy', `${Math.sin(angle) * distance + Math.random() * 6}px`);
      drop.style.setProperty('--dur', `${380 + Math.random() * 320}ms`);
      drop.style.setProperty('--delay', `${Math.random() * 90}ms`);
      mist.appendChild(drop);
    }

    document.body.appendChild(mist);
    setTimeout(() => mist.remove(), 1100);
  }

  window.addEventListener(
    'mousedown',
    (event) => {
      if (event.button !== 0 || !bottle.classList.contains('is-active')) return;
      setState('is-pressed', true);
      spray(event.clientX, event.clientY);
    },
    { passive: true }
  );
  window.addEventListener('mouseup', () => setState('is-pressed', false), { passive: true });
})();
